import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Building2, Lock } from 'lucide-react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface IOrganizationDetails {
  name: string;
  id: string;
  industry: string;
  location: string;
  timeZone: string;
  hasEditEnabled?: boolean;
  onSave?: (data: Omit<IOrganizationDetails, 'hasEditEnabled' | 'onSave'>) => void;
}

export const OrganizationDetails: React.FC<IOrganizationDetails> = (props) => {
  const { name, id, industry, location, timeZone, onSave } = props;

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData: { name, id, industry, location, timeZone },
    onSave: async (data) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onSave) {
        onSave(data);
      }
    },
  });

  return (
    <SettingsCard
      title="Organization Details"
      description="Manage your organization information"
      icon={<Building2 size={20} />}
      isEditing={isEditing}
      isSaving={isSaving}
      onEditToggle={handleEditToggle}
      onSave={handleSave}
    >
      {isEditing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <FloatingLabelInput
              label="Organization Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
            <FloatingLabelInput
              label="Industry"
              value={formData.industry}
              onChange={(e) => handleInputChange('industry', e.target.value)}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <FloatingLabelInput
              label="Location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
            />
            <FloatingLabelInput
              label="Time Zone"
              value={formData.timeZone}
              onChange={(e) => handleInputChange('timeZone', e.target.value)}
            />
          </Box>

          {/* Organization ID - Read Only */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: '8px',
              bgcolor: 'action.hover',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Lock size={15} style={{ color: '#64748b' }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>
                Organization ID
              </Typography>
            </Box>
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'text.primary',
                bgcolor: 'background.paper',
                borderColor: 'divider',
                borderRadius: '6px',
              }}
            >
              {formData.id}
            </Paper>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}>
              This ID is automatically generated and cannot be changed.
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Organization Name" text={formData.name} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Industry" text={formData.industry} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Location" text={formData.location} />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
            <DetailedItem label="Time Zone" text={formData.timeZone} />
          </Paper>
          <Box sx={{ gridColumn: { md: 'span 2' } }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '8px',
                bgcolor: 'rgba(59, 130, 246, 0.04)',
                borderColor: 'rgba(59, 130, 246, 0.2)',
              }}
            >
              <DetailedItem
                label="Organization ID"
                text={formData.id}
                helpertext="This ID is automatically generated and cannot be changed."
              />
            </Paper>
          </Box>
        </Box>
      )}
    </SettingsCard>
  );
};
